import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { COMPANY_INFO } from "../data/siteData";

export default function PrivacyPage() {
  return (
    <>
      <Helmet>
        <title>Privatlivspolitik | NF Polering</title>
        <meta
          name="description"
          content="Læs hvordan NF Polering behandler og beskytter dine personoplysninger. Fuld GDPR-kompatibel privatlivspolitik for både private og erhvervskunder."
        />
        <meta property="og:title" content="Privatlivspolitik | NF Polering" />
        <meta
          property="og:description"
          content="Læs hvordan NF Polering behandler og beskytter dine personoplysninger. Fuld GDPR-kompatibel privatlivspolitik for både private og erhvervskunder."
        />
        <link rel="canonical" href="https://nf-polering.dk/privatlivspolitik" />
      </Helmet>

      <div className="bg-slate-50 text-slate-900 py-14 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 text-cyan-600 text-xs font-bold uppercase tracking-wider mb-2">
            <i className="fa-solid fa-shield-halved" aria-hidden="true" />
            <span>Persondata &amp; GDPR</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            Privatlivspolitik for NF Polering
          </h1>
          <p className="mt-3 text-slate-600 text-sm">
            <strong>Senest opdateret: 19/11/2025</strong>
          </p>
        </div>
      </div>

      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-slate-700 space-y-8 leading-relaxed">
          <p className="text-base font-medium text-slate-800">
            Denne privatlivspolitik beskriver, hvordan <strong>NF Polering</strong> behandler personoplysninger, når du kontakter os via vores hjemmeside.
          </p>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">1. Dataansvarlig</h2>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm space-y-1">
              <p><strong>NF Polering</strong></p>
              <p>Slagelse 4200</p>
              <p>CVR: 35 01 70 89</p>
              <p>Mail: <a href="mailto:kontakt@nf-polering.dk" className="text-cyan-600 font-medium">kontakt@nf-polering.dk</a></p>
              <p>Tlf.: <a href="tel:30146070" className="text-cyan-600 font-medium">+45 30 14 60 70</a></p>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">2. Hvilke oplysninger indsamles</h2>
            <p>Når du sender en forespørgsel via kontaktformularen, indsamler vi:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Navn</li>
              <li>Adresse / by</li>
              <li>Telefonnummer</li>
              <li>Ønsket service (f.eks. antal vinduer, frekvens)</li>
              <li>Din besked</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">3. Hvordan oplysninger indsamles</h2>
            <p>Oplysningerne indsamles udelukkende via vores <strong>kontaktformular</strong> på hjemmesiden.</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">4. Formål med behandlingen</h2>
            <p>Vi behandler dine oplysninger til følgende formål:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Udarbejdelse af tilbud</li>
              <li>Kontakt og dialog med dig som potentiel eller eksisterende kunde</li>
              <li>Generel kundehåndtering</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">5. Behandlingsgrundlag</h2>
            <p>Behandlingen sker på følgende grundlag:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Artikel 6(1)(b)</strong> – nødvendigt for at kunne opfylde din forespørgsel og indgå en aftale</li>
              <li><strong>Artikel 6(1)(f)</strong> – legitim interesse i at besvare henvendelser og drive virksomheden</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">6. Modtagere af personoplysninger</h2>
            <p>Dine oplysninger behandles af:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Internt personale hos NF Polering</li>
              <li>Vores mail- og hostingudbyder (Hostinger)</li>
            </ul>
            <p className="mt-2">Vi sælger ikke personoplysninger til tredjepart.</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">7. Brug af databehandlere</h2>
            <p>Vi anvender Hostinger som databehandler til drift af hjemmeside og mail. Databehandleraftale følger Hostingers standardvilkår.</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">8. Overførsel til tredjelande</h2>
            <p>Vi overfører ikke personoplysninger uden for EU/EØS.</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">9. Opbevaringsperiode</h2>
            <p>Oplysninger opbevares efter <strong>branchestandarder</strong>, typisk:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Forespørgsler opbevares så længe de er relevante</li>
              <li>Som kunde opbevares oplysninger i op til 5 år jf. bogføringsloven</li>
            </ul>
            <p className="mt-2">Oplysninger slettes herefter.</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">10. Cookies og tracking</h2>
            <p>Hjemmesiden anvender cookies til statistik og analyse via Google Analytics.</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">11. Dine rettigheder</h2>
            <p>Du har ret til:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Indsigt i hvilke oplysninger vi har om dig</li>
              <li>At få rettet forkerte oplysninger</li>
              <li>At få slettet oplysninger (i det omfang lovgivningen tillader det)</li>
              <li>At gøre indsigelse</li>
              <li>At få udleveret dine data (dataportabilitet)</li>
            </ul>
            <p className="mt-2">Henvendelser om dette sendes til: <strong>kontakt@nf-polering.dk</strong></p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">12. Klage</h2>
            <p>Hvis du mener, at dine data behandles i strid med lovgivningen, kan du klage til:</p>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm mt-2">
              <p><strong>Datatilsynet</strong></p>
              <p>Carl Jacobsens Vej 35</p>
              <p>2500 Valby</p>
              <p><a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-cyan-600">www.datatilsynet.dk</a></p>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-200">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-600 text-white font-bold text-sm hover:bg-cyan-700 transition-colors"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>Tilbage til forsiden</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
